import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/images';
import GallerySection from '../components/GallerySection';

export default function CaseDetailPage() {
  const { slug } = useParams();
  const [caseItem, setCaseItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadCase() {
      try {
        const cases = await fetchCollection('cases');
        const match = cases.find(
          c => c.identifier === slug || c.slug?._ === slug || c.id === slug
        );
        if (isMounted) {
          setCaseItem(match || cases[0] || null);
          if (match) {
            document.title = `${match.title} - Health4media.dk`;
          }
        }
      } catch (err) {
        console.error('Error loading case detail:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadCase();
    return () => { isMounted = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-300 border-t-sky-600 mb-4" />
        <p className="text-slate-600 text-sm">Henter case...</p>
      </div>
    );
  }

  if (!caseItem) {
    return (
      <div className="py-24 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Case ikke fundet</h1>
        <p className="text-slate-600 mt-2">Den efterspurgte case kunne ikke findes.</p>
        <Link to="/cases" className="mt-4 inline-block px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold">
          Tilbage til cases
        </Link>
      </div>
    );
  }

  const imgUrl = caseItem.featured_image?.[0] || '';

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* CASE DETAIL HEADER */}
      <section className="bg-slate-900 text-white py-10 sm:py-16 md:py-20 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
            {caseItem.title}
          </h1>

          <p className="mt-3 sm:mt-4 text-sm sm:text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
            {caseItem.summary}
          </p>

          <div className="mt-4 sm:mt-6 flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-slate-300">
            <span>
              <span className="text-white font-semibold">Format:</span> {caseItem.format || 'Klinisk video & shortform'}
            </span>
            <span>
              <span className="text-white font-semibold">Målgruppe:</span> Patienter & Lokalområde
            </span>
          </div>

        </div>
      </section>

      {/* FEATURED VISUAL */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-10">
        <div className="overflow-hidden shadow-xl border border-slate-200 bg-white aspect-16/9 max-w-5xl">
          {imgUrl && (
            <img
              src={getOptimizedImageUrl(imgUrl, { width: 1200, height: 675, fit: 'cover' })}
              alt={caseItem.title}
              className="w-full h-full object-cover"
            />
          )}
        </div>
      </div>

      {/* CASE CONTENT BREAKDOWN */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          
          {/* Challenge Section */}
          {caseItem.challenge && (
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold text-slate-900 uppercase tracking-wider mb-3">
                <i className="fa-solid fa-circle-exclamation text-xs text-slate-900" aria-hidden="true" />
                <span>Udfordring & Udgangspunkt</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Hvad var situationen for klinikken?
              </h2>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4">
                {caseItem.challenge}
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Mange klinikker oplever, at patienter søger svar på sociale medier eller Google, hvor fejlinformation og uigennemskuelige råd florerer. Uden en aktiv visuel tilstedeværelse forbliver klinikkens reelle ekspertise usynlig for de borgere i lokalområdet, der har størst behov for professionel behandling.
              </p>
            </div>
          )}

          {/* Solution Section */}
          {caseItem.solution && (
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold text-slate-900 uppercase tracking-wider mb-3">
                <i className="fa-solid fa-lightbulb text-xs text-slate-900" aria-hidden="true" />
                <span>Den strategiske løsning</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Hvordan blev forløbet og redigeringen aktiveret?
              </h2>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4">
                {caseItem.solution}
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Vi tilrettelagde en struktureret online plan med konkrete optageguides og emneforslag, så behandlerne nemt selv kunne optage klip med mobilen direkte i klinikken uden at forstyrre driften. Behandlerne blev guidet trygt online, og efterfølgende blev det indsendte råmateriale professionelt klippet og opsat til fængende shortform formater med tydelige undertekster og udgivet via en fast Meta postkalender.
              </p>
            </div>
          )}

          {/* Result Section */}
          {caseItem.results && (
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold text-slate-900 uppercase tracking-wider mb-3">
                <i className="fa-solid fa-chart-line text-xs text-slate-900" aria-hidden="true" />
                <span>Resultat & Effekt</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Hvilken forskel gjorde det for klinikken?
              </h2>
              <p className="text-base sm:text-lg text-slate-900 leading-relaxed font-semibold mb-4">
                {caseItem.results}
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Resultatet er en markant stærkere positionering i lokalområdet, hvor patienterne allerede ved første konsultation føler et kendskab til behandlerne og klinikkens metoder. Videoerne fungerer som et vedvarende digitalt visitkort, der kontinuerligt opbygger tillid og tiltrækker nye, motiverede patienter.
              </p>
            </div>
          )}

        </div>
      </section>

      {/* FINAL CASE CTA */}
      <section className="py-16 bg-slate-900 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ønsker du et lignende resultat for din praksis?
          </h3>
          <p className="text-slate-300 mb-8 text-base">
            Vi rådgiver dig om det optimale videoformat til netop dit sundhedsfaglige speciale.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold transition shadow-lg shadow-sky-950/40"
          >
            <span>Book en samtale</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* CONNECTED CMS GALLERY (Nederst over footer) */}
      <GallerySection
        gallerySlug={caseItem.gallery_slug || 'sundhedscases-galleri'}
        title="Tilknyttet galleri til denne case"
        subtitle="Billeder fra optagelser og arbejdet bag videoerne."
        className="bg-white border-t border-slate-200"
      />

    </div>
  );
}
